/**
 * The browser side of Web Push, with no API calls: `shared/api/client` drops
 * the subscription of a dead session through here and must not import the
 * notifications API that is built on top of it.
 */

export const PUSH_SW_PATH = '/push-sw.js'

export const isWebPushSupported = () =>
  typeof window !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  Boolean(navigator.serviceWorker) &&
  'PushManager' in window &&
  'Notification' in window

export const urlBase64ToUint8Array = (base64: string) => {
  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    '='
  )
  const raw = atob(padded.replaceAll('-', '+').replaceAll('_', '/'))
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i)
  }
  return output
}

export const encodeKey = (buffer: ArrayBuffer | null) => {
  if (!buffer) {
    return ''
  }
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary)
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replace(/=+$/, '')
}

/**
 * True when the subscription was made with this VAPID public key. A browser
 * that does not expose the key gets the benefit of the doubt: resubscribing
 * on every sync would mint a new endpoint each time.
 */
export function hasApplicationServerKey(
  subscription: PushSubscription,
  vapidPublicKey: string
): boolean {
  const current = subscription.options?.applicationServerKey
  if (!current) {
    return true
  }

  const expected = urlBase64ToUint8Array(vapidPublicKey)
  const actual = new Uint8Array(current)

  return (
    actual.length === expected.length &&
    actual.every((byte, i) => byte === expected[i])
  )
}

/** The registration of the push worker, without registering it. */
export async function getPushRegistration() {
  if (!isWebPushSupported()) {
    return undefined
  }
  try {
    return await navigator.serviceWorker.getRegistration(PUSH_SW_PATH)
  } catch {
    return undefined
  }
}

export async function getBrowserPushSubscription() {
  const registration = await getPushRegistration()
  try {
    return (await registration?.pushManager.getSubscription()) ?? null
  } catch {
    return null
  }
}

/**
 * Unsubscribes this browser from the push service. Used when the session is
 * already gone and the server cannot be told: the push service answers 410 to
 * the next message for this endpoint and the server deletes it then.
 *
 * Returns the dropped endpoint. Never throws.
 */
export async function dropBrowserPushSubscription(): Promise<
  string | undefined
> {
  const subscription = await getBrowserPushSubscription()
  if (!subscription) {
    return undefined
  }

  try {
    await subscription.unsubscribe()
    return subscription.endpoint
  } catch {
    return undefined
  }
}
